import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Switch, Route } from "react-router-dom";
import { useSelector } from "react-redux";

import SideBar from "./home/SideBar";
import CustomerLocationStrip from "./home/CustomerLocationStrip";
import CustomerToolbar from "./home/CustomerToolbar";
import CustomerDirectoryTable from "./tables/CustomerDirectoryTable";
import Busy from "./Busy";
import { serverUrl } from "../serverUrl";
import dots from "../assets/dots.svg";

const readJson = async (response) => {
  const raw = await response.text();
  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch (_) {
    return raw;
  }
};

const rowsFromResponse = (data, keys) => {
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return rowsFromResponse(JSON.parse(data), keys);
    } catch (_) {
      return [];
    }
  }

  const nested = data?.data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(nested?.[key])) return nested[key];
  }

  return Array.isArray(nested) ? nested : data?.rows || data?.items || [];
};

const getCompanyId = (company) => String(company?.company_id ?? company?.id ?? "");

const hasVisibleAreas = (company) =>
  company?.area_visible === true ||
  company?.area_visible === 1 ||
  String(company?.area_visible) === "1";

const normalizeCompanies = (rows) =>
  rows
    .filter((company) => {
      const name = String(company?.company_name || "").toLowerCase();
      return company && !name.includes("add new location");
    })
    .map((company) => ({
      ...company,
      company_id: getCompanyId(company),
      company_name: company.company_name || company.name || getCompanyId(company),
    }));

const normalizeLocations = (rows) =>
  rows
    .map((location) => ({
      ...location,
      area_id: String(location.area_id ?? location.shop_id ?? location.location_id ?? location.id ?? ""),
      area_name:
        location.area_name ||
        location.shop_name ||
        location.location_name ||
        location.company_name ||
        location.name ||
        "",
    }))
    .filter((location) => location.area_id || location.area_name);

const Home = () => {
  const ui = useSelector((state) => state.ui);
  const user = useSelector((state) => state.userState.user);

  const [showSideBar, setShowSideBar] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [tab, setTab] = useState("types");
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");

  const selectedCompany = useMemo(
    () => companies.find((company) => getCompanyId(company) === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const selectedArea = useMemo(
    () => locations.find((location) => String(location.area_id) === String(selectedAreaId)) || null,
    [locations, selectedAreaId]
  );

  const loadAreas = useCallback(async (company, signal) => {
    if (!company || !user?.user_id) return [];

    if (!hasVisibleAreas(company)) {
      setLocations([]);
      setSelectedAreaId("0");
      return [];
    }

    const response = await fetch(`${serverUrl}/api/list/resto/areasK`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token_user_id: user.user_id,
        token_company_id: getCompanyId(company),
      }),
      signal,
    });

    if (!response.ok) throw new Error(`areasK failed (${response.status})`);

    const data = await readJson(response);
    const nextLocations = normalizeLocations(rowsFromResponse(data, ["areas", "shops", "locations"]));
    setLocations(nextLocations);
    setSelectedAreaId(nextLocations[0]?.area_id || "");

    return nextLocations;
  }, [user?.user_id]);

  const loadCompanies = useCallback(async (signal) => {
    if (!user?.user_id) return;

    setLoading(true);
    setError("");
    setCompanies([]);
    setLocations([]);
    setSelectedCompanyId("");
    setSelectedAreaId("");

    try {
      const response = await fetch(`${serverUrl}/api/list/restoK`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token_user_id: user.user_id }),
        signal,
      });

      if (!response.ok) throw new Error(`restoK failed (${response.status})`);

      const data = await readJson(response);
      const nextCompanies = normalizeCompanies(rowsFromResponse(data, ["companies"]));
      const firstCompany = nextCompanies[0] || null;

      setCompanies(nextCompanies);

      if (!firstCompany) {
        setError("No companies returned from restoK");
        return;
      }

      setSelectedCompanyId(getCompanyId(firstCompany));
      await loadAreas(firstCompany, signal);
    } catch (err) {
      if (err.name !== "AbortError") {
        setError(err.message || "Unable to load companies");
      }
    } finally {
      setLoading(false);
    }
  }, [loadAreas, user?.user_id]);

  useEffect(() => {
    if (!user?.user_id) return undefined;

    const controller = new AbortController();
    loadCompanies(controller.signal);
    return () => controller.abort();
  }, [loadCompanies, user?.user_id]);

  const handleCompanyChange = async (companyId) => {
    const company = companies.find((item) => getCompanyId(item) === companyId);
    if (!company) return;

    setLoading(true);
    setError("");
    setSelectedCompanyId(companyId);
    setLocations([]);
    setSelectedAreaId("");

    try {
      await loadAreas(company);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err.message || "Unable to load locations");
    } finally {
      setLoading(false);
    }
  };

  const handleAreaChange = (areaId) => {
    setSelectedAreaId(areaId);
    setRefreshKey((current) => current + 1);
  };

  const handleSync = async () => {
    if (!selectedCompany || !user?.user_id) return;

    setSyncing(true);
    setError("");

    try {
      const response = await fetch(`${serverUrl}/api/general`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operation_id: "CUSTOMER_DISC_CUSTOM_SYNC",
          integration_name: "custom_k_discount",
          token_company_id: getCompanyId(selectedCompany),
          token_user_id: user.user_id,
        }),
      });

      if (!response.ok) throw new Error(`Sync failed (${response.status})`);

      await handleCompanyChange(getCompanyId(selectedCompany));
    } catch (err) {
      setError(err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="home">
      {showSideBar && (
        <SideBar
          companies={companies}
          selectedCompanyId={selectedCompanyId}
          onCompanyChange={handleCompanyChange}
        />
      )}

      <div className="centered side-bar-toggle" onClick={() => setShowSideBar((old) => !old)}>
        <img src={dots} alt="burger dots" />
      </div>

      <Switch>
        <Route path="/" exact>
          <div className="main-section relative">
            {ui.busy && <Busy />}

            <CustomerLocationStrip
              locations={locations}
              selectedCompanyId={selectedCompanyId}
              selectedAreaId={selectedAreaId}
              loading={loading}
              syncing={syncing}
              error={error}
              onAreaChange={handleAreaChange}
              onSync={handleSync}
            />

            <CustomerToolbar
              activeTab={tab}
              theme={ui.theme}
              onTabChange={(nextTab) => {
                setTab(nextTab);
                setRefreshKey((current) => current + 1);
              }}
            />

            <div className="table-content">
              <CustomerDirectoryTable
                key={`${getCompanyId(selectedCompany)}:${selectedAreaId}:${tab}`}
                mode={tab}
                companyId={getCompanyId(selectedCompany)}
                areaId={selectedArea?.area_id || selectedAreaId || ""}
                direct={!hasVisibleAreas(selectedCompany)}
                refreshKey={refreshKey}
              />
            </div>
          </div>
        </Route>
      </Switch>
    </div>
  );
};

export default Home;
